import React, { useState, useEffect, useCallback } from 'react';
import { History, CheckCircle2, AlertTriangle, XCircle, Undo2, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getSyncHistory, clearSyncHistory, SYNC_HISTORY_CHANGED_EVENT } from '../utils/syncHistory';
import { Button } from './ui/button';

function relTime(ts, lang) {
  if (!ts) return '';
  try {
    const rtf = new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'auto' });
    const sec = Math.round((Number(ts) - Date.now()) / 1000);
    const abs = Math.abs(sec);
    if (abs < 60) return rtf.format(sec, 'second');
    const min = Math.round(sec / 60);
    if (Math.abs(min) < 60) return rtf.format(min, 'minute');
    const hr = Math.round(min / 60);
    if (Math.abs(hr) < 24) return rtf.format(hr, 'hour');
    return rtf.format(Math.round(hr / 24), 'day');
  } catch { return ''; }
}

const STATUS = {
  synced: { icon: CheckCircle2, color: 'hsl(143, 45%, 38%)', key: 'syncStatusSynced', fallback: 'Synced' },
  conflict: { icon: AlertTriangle, color: 'hsl(35, 80%, 42%)', key: 'syncStatusConflict', fallback: 'Conflict' },
  gaveup: { icon: XCircle, color: 'hsl(0, 60%, 45%)', key: 'syncStatusGaveup', fallback: 'Failed' },
  discarded: { icon: Undo2, color: 'hsl(125, 12%, 45%)', key: 'syncStatusDiscarded', fallback: 'Discarded' },
};

const OP_LABEL = {
  edit: 'pendingOpEdit', archive: 'pendingOpArchive', delete: 'pendingOpDelete',
  'bulk-update': 'pendingOpBulkUpdate', 'bulk-delete': 'pendingOpBulkDelete',
};

/**
 * Settings section (Drive mode) showing the recent sync-activity log.
 * Reads localStorage directly (own instance is fine — it's read-only).
 * Hidden when there's no history.
 */
export const SyncHistorySettings = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const userKey = user?.email || user?.id || 'anon';
  const [items, setItems] = useState([]);

  const refresh = useCallback(() => setItems(getSyncHistory(userKey)), [userKey]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener(SYNC_HISTORY_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(SYNC_HISTORY_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [refresh]);

  if (items.length === 0) return null;

  const opText = (item) => {
    const name = item.name || t('unnamedPlant') || 'a plant';
    const tmpl = t(OP_LABEL[item.op] || 'pendingOpEdit') || 'Edit "{name}"';
    return tmpl.replace('{name}', name).replace('{count}', '').replace('{plantWord}', '').trim();
  };

  return (
    <div className="space-y-2 border-t pt-4" data-testid="sync-history-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
          <History className="w-4 h-4" aria-hidden="true" />
          {t('syncHistoryTitle') || 'Recent sync activity'}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { clearSyncHistory(userKey); }}
          className="gap-1 text-xs"
          data-testid="sync-history-clear"
        >
          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
          {t('syncHistoryClear') || 'Clear'}
        </Button>
      </div>
      <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="sync-history-list">
        {items.slice(0, 12).map((item, i) => {
          const meta = STATUS[item.status] || STATUS.synced;
          const Icon = meta.icon;
          const statusLabel = t(meta.key) || meta.fallback;
          return (
            <li key={`${item.at}-${i}`} className="flex items-center gap-2 text-sm" data-testid="sync-history-item">
              <Icon
                className="w-4 h-4 flex-shrink-0"
                style={{ color: meta.color }}
                aria-hidden="true"
              >
                <title>{statusLabel}</title>
              </Icon>
              <span className="flex-1 min-w-0 truncate" style={{ color: 'hsl(143, 28%, 30%)' }}>
                <span className="sr-only">{statusLabel}: </span>{opText(item)}
              </span>
              <span className="flex-shrink-0 text-xs" style={{ color: 'hsl(125, 12%, 48%)' }}>
                {relTime(item.at, language)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SyncHistorySettings;
